/**
 * @file IconButton 图标按钮
 * @description 仅含图标的方形紧凑按钮，固定 size-8（32px）；无 size API。
 *
 * @whenToUse 工具栏、表格行操作、关闭/更多等只需图标的点击入口。
 * @whenNotToUse
 * 1. 需要文案的主操作（用 Button）。
 * 2. 视口右下角全局悬浮入口（用 FloatButton）。
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { LoaderCircle } from 'lucide-react'
import { cn } from '@/lib/cn.ts'

/**
 * 视觉变体：
 * - ghost：默认透明，hover 显现背景
 * - outline：带边框
 * - solid：带实心背景
 */
export type IconButtonVariant = 'ghost' | 'outline' | 'solid'

export type IconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children' | 'aria-label'
> & {
  /** 按钮内图标（lucide-react，建议 size-4） */
  icon: ReactNode
  /** 视觉变体 */
  variant?: IconButtonVariant
  /** 加载中（自动 disabled，图标替换为旋转） */
  loading?: boolean
  /** 无障碍名称（图标按钮必填） */
  'aria-label': string
}

const variantClass: Record<IconButtonVariant, string> = {
  ghost:
    'border-transparent bg-transparent text-muted hover:bg-accent/25 hover:text-primary focus-visible:bg-accent/25 focus-visible:text-primary',
  outline:
    'border-border bg-transparent text-foreground hover:opacity-90 hover:text-primary focus-visible:opacity-90 focus-visible:text-primary',
  solid:
    'border-primary bg-primary text-primary-foreground hover:opacity-90 hover:text-primary focus-visible:opacity-90 focus-visible:text-primary',
}

/**
 * 仅图标方形按钮（固定 size-8 / 32px）
 */
export function IconButton({
  icon,
  variant = 'ghost',
  loading = false,
  className,
  disabled,
  type = 'button',
  'aria-label': ariaLabel,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      disabled={disabled || loading}
      className={cn(
        'inline-flex size-6.5 shrink-0 items-center justify-center rounded-lg border transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50',
        variantClass[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <LoaderCircle className="size-3.5 animate-spin" aria-hidden />
      ) : (
        icon
      )}
    </button>
  )
}
